import type { ReactNode } from 'react';
import { RefreshCcw } from 'lucide-react';
import {
  SiteContentProvider,
  useSiteContentQuery,
} from '@personal-portfolio/queries';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@/shared/ui';
import { shellCopy } from '../shell-copy';

/**
 * Editorial copy has no bundled fallback, so the app cannot render until the
 * CMS has answered. This gate is the one place that waits — everything below
 * reads copy synchronously through useSiteContent().
 *
 * A restored cache counts as an answer: a returning or offline visitor gets
 * the app immediately and never sees the splash, while the query revalidates
 * in the background (staleTime 0) and swaps in newer copy when it arrives.
 * The splash therefore appears only on a genuinely cold first visit.
 */
export function SiteContentGate({ children }: { children: ReactNode }) {
  const { data, isPending, isPaused, refetch, isFetching } =
    useSiteContentQuery();

  if (data) {
    return <SiteContentProvider value={data}>{children}</SiteContentProvider>;
  }

  // A 404 resolves to null rather than throwing, so "no data and settled" is
  // as much a failure as a network error: there is nothing to render.
  //
  // `isPaused` has to break the spinner too. TanStack stops retrying whenever
  // the window loses focus or the browser goes offline (query-core's retryer
  // gates continuation on `focusManager.isFocused() && onlineManager
  // .isOnline()` — networkMode does not exempt it). A paused query keeps
  // status 'pending' indefinitely, so testing `isPending` alone leaves this
  // gate spinning forever with no way out, and this gate blocks the entire
  // app. Paused with nothing cached is indistinguishable from failed as far
  // as the visitor is concerned: show them the retry.
  if (isPending && !isPaused) {
    return (
      <main
        className="flex min-h-dvh items-center justify-center"
        aria-busy="true"
        aria-label={shellCopy.loading}
      >
        <Spinner className="size-8" />
      </main>
    );
  }

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <Empty>
        <EmptyHeader>
          {/* Local asset, not the CMS avatar: this renders precisely when the
              CMS has not answered, so profile media is unavailable. Grayscale
              reads the state without another icon — the site's own mark,
              drained of colour. */}
          <EmptyMedia>
            <Avatar size="lg" className="grayscale">
              <AvatarImage src="/web-app-manifest-192x192.png" alt="" />
              <AvatarFallback>BY</AvatarFallback>
            </Avatar>
          </EmptyMedia>
          <EmptyTitle>{shellCopy.unavailableTitle}</EmptyTitle>
          <EmptyDescription>{shellCopy.unavailableBody}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button onPress={() => void refetch()} isDisabled={isFetching}>
            <RefreshCcw />
            {shellCopy.retry}
          </Button>
        </EmptyContent>
      </Empty>
    </main>
  );
}
